import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AppProvider, useApp } from '../../context/AppContext';
import BabylonScene from '../Editor/BabylonScene';
import ViewCube from '../Editor/ViewCube';
import ViewerGate from './ViewerGate';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { 
  Eye, ArrowUp, ArrowDown, ArrowLeft, ArrowRight,
  Grid3X3, Maximize2, Home, Camera, Zap, ZapOff,
  ChevronLeft, ChevronRight, Lightbulb, Ruler, Power,
  Hand, Move3d
} from 'lucide-react';

const ViewerContent = ({ shareToken }) => {
  const { 
    worldSettings, setWorldSettings,
    projectors, sceneObjects,
    setProjectors, setSceneObjects,
    setProjectSettings, measurements, setMeasurements,
    controlMode, setControlMode,
    viewPreset: globalViewPreset, setViewPreset: setGlobalViewPreset
  } = useApp();
  
  const [showControls, setShowControls] = useState(true);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [viewPreset, setViewPreset] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedCameras, setSavedCameras] = useState([]);
  const [showMeasurements, setShowMeasurements] = useState(true);

  // Viewer access-gate state. A `viewer_session_token` stored in localStorage
  // (keyed by share_token) unlocks the shared viewer endpoint; without it the
  // gate form is shown and no preview data is loaded. The project name is
  // fetched via a lightweight public /meta endpoint so the gate can display
  // it even before registration is complete.
  const sessionKey = `viewer_session_${shareToken}`;
  const [viewerSessionToken, setViewerSessionToken] = useState(() => {
    try { return localStorage.getItem(sessionKey) || null; } catch { return null; }
  });
  const [gateProjectName, setGateProjectName] = useState('');

  useEffect(() => {
    if (!shareToken || viewerSessionToken) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    (async () => {
      try {
        const r = await fetch(`${API}/api/viewer/${shareToken}/meta`);
        if (r.ok) {
          const d = await r.json();
          setGateProjectName(d.name || '');
        }
      } catch { /* ignore — gate still works without a name */ }
    })();
  }, [shareToken, viewerSessionToken]);

  // ViewCube uses the AppContext viewPreset setter; sync it to local state so
  // BabylonScene (which consumes the local prop) reacts to cube clicks.
  useEffect(() => {
    if (globalViewPreset) {
      setViewPreset(globalViewPreset);
      setGlobalViewPreset(null);
    }
  }, [globalViewPreset, setGlobalViewPreset]);

  // Toggle measurement visibility in the scene
  const toggleMeasurementVisibility = useCallback((visible) => {
    setShowMeasurements(visible);
    setMeasurements(prev => prev.map(m => ({ ...m, visible })));
  }, [setMeasurements]);

  // Track which fields the viewer has toggled locally. Polling MUST NOT
  // overwrite these or the user's Beam/Power/Border/Grid switches would flip
  // back every 3 seconds (they are view-only ephemerals, not persisted).
  const VIEWER_PROJ_FLAGS = ['power_on', 'show_beam', 'show_border'];
  const viewerOverridesRef = React.useRef({
    projectors: {},          // { [projId]: { power_on, show_beam, show_border } }
    worldGridOverride: null  // true / false / null
  });

  useEffect(() => {
    if (!shareToken) { setLoadError('No share token provided'); setLoading(false); return; }
    // Gate: don't fetch project data until the viewer has a registered session.
    if (!viewerSessionToken) return;

    const API = process.env.REACT_APP_BACKEND_URL;
    const loadSharedProject = async () => {
      try {
        const res = await fetch(`${API}/api/viewer/${shareToken}`, {
          headers: { 'X-Viewer-Session': viewerSessionToken }
        });
        if (res.status === 403) {
          // Session revoked / invalid — clear and re-prompt the gate form.
          try { localStorage.removeItem(sessionKey); } catch { /* ignore */ }
          setViewerSessionToken(null);
          return;
        }
        if (!res.ok) { if (loading) { setLoadError('Project not found or link has expired'); setLoading(false); } return; }
        const data = await res.json();
        setProjectName(data.name || 'Shared Project');
        if (data.projectors) {
          // Merge incoming projector data with any viewer-side toggle overrides.
          const overrides = viewerOverridesRef.current.projectors;
          const merged = data.projectors.map(p => {
            const ov = overrides[p.id];
            return ov ? { ...p, ...ov } : p;
          });
          setProjectors(merged);
        }
        if (data.scene_objects) setSceneObjects(data.scene_objects);
        if (data.world_settings) {
          const ws = { ...data.world_settings };
          if (viewerOverridesRef.current.worldGridOverride !== null) {
            ws.showGrid = viewerOverridesRef.current.worldGridOverride;
          }
          setWorldSettings(ws);
        }
        if (data.project_settings) setProjectSettings(data.project_settings);
        if (data.saved_cameras) setSavedCameras(data.saved_cameras);
        if (data.measurements) setMeasurements(data.measurements);
        if (loading) setLoading(false);
      } catch {
        if (loading) { setLoadError('Failed to load shared project'); setLoading(false); }
      }
    };
    loadSharedProject();
    const interval = setInterval(loadSharedProject, 3000);
    return () => clearInterval(interval);
  }, [shareToken, viewerSessionToken]);

  const handleViewChange = useCallback((view) => setViewPreset(view), []);
  const handleViewPresetReset = useCallback(() => setViewPreset(null), []);

  // Record a local viewer-only override for a projector flag so the next poll
  // preserves it.
  const recordProjectorOverride = (projId, key, value) => {
    const ov = viewerOverridesRef.current.projectors;
    ov[projId] = { ...(ov[projId] || {}), [key]: value };
  };

  const toggleGrid = () => {
    const next = !worldSettings?.showGrid;
    viewerOverridesRef.current.worldGridOverride = next;
    setWorldSettings({ ...worldSettings, showGrid: next });
  };

  const toggleProjectorPower = (projId) => {
    setProjectors(prev => prev.map(p => {
      if (p.id !== projId) return p;
      const next = !p.power_on;
      recordProjectorOverride(projId, 'power_on', next);
      return { ...p, power_on: next };
    }));
  };
  const toggleAllProjectorsPower = () => {
    const allOn = projectors.every(p => p.power_on !== false);
    const next = !allOn;
    setProjectors(prev => prev.map(p => {
      recordProjectorOverride(p.id, 'power_on', next);
      return { ...p, power_on: next };
    }));
  };
  const toggleProjectorBeam = (projId) => {
    setProjectors(prev => prev.map(p => {
      if (p.id !== projId) return p;
      const next = !p.show_beam;
      recordProjectorOverride(projId, 'show_beam', next);
      return { ...p, show_beam: next };
    }));
  };
  const toggleAllBeams = () => {
    const allBeams = projectors.every(p => p.show_beam !== false);
    const next = !allBeams;
    setProjectors(prev => prev.map(p => {
      recordProjectorOverride(p.id, 'show_beam', next);
      return { ...p, show_beam: next };
    }));
  };
  const toggleProjectorBorder = (projId) => {
    setProjectors(prev => prev.map(p => {
      if (p.id !== projId) return p;
      const next = !p.show_border;
      recordProjectorOverride(projId, 'show_border', next);
      return { ...p, show_border: next };
    }));
  };

  const switchToCamera = (cam) => setViewPreset({ type: 'camera', data: cam });

  // Viewer Access Gate — shown before anything else when the viewer has no
  // registered session yet. It calls /register which returns a session token;
  // we persist it to localStorage and drop the gate. Blocks all preview data
  // fetching (the effect above is gated on `viewerSessionToken`).
  if (!viewerSessionToken) {
    return (
      <ViewerGate
        shareToken={shareToken}
        projectName={gateProjectName}
        onRegistered={(tk) => setViewerSessionToken(tk)}
      />
    );
  }

  if (loading) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400">Loading shared project...</div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="w-screen h-screen bg-zinc-950 flex flex-col items-center justify-center gap-4">
        <Eye className="w-12 h-12 text-zinc-600" />
        <div className="text-zinc-400 text-lg">{loadError}</div>
        <div className="text-zinc-600 text-sm">The share link may be invalid or the project may have been deleted.</div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-zinc-950 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 h-10 bg-zinc-900/90 backdrop-blur border-b border-zinc-800 flex items-center justify-between px-4 z-10">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-fuchsia-400" />
          <span className="text-sm font-medium text-white">IAV Viewer</span>
          {projectName && <span className="text-xs text-zinc-400 ml-2">— {projectName}</span>}
          <span className="text-xs text-zinc-600 ml-2">View Only</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>{projectors?.length || 0} Projector(s)</span>
          <span className="text-zinc-600">|</span>
          <span>{sceneObjects?.length || 0} Object(s)</span>
        </div>
      </div>

      {/* 3D Scene */}
      <div className="absolute inset-0 pt-10">
        <BabylonScene
          viewPreset={viewPreset}
          onViewPresetReset={handleViewPresetReset}
          controlMode={controlMode}
        />
        {/* Interactive 3D ViewCube — click faces/corners to snap to a view. */}
        <ViewCube />
      </div>

      {/* ═══ LEFT PANEL: Projectors, Beams, Measurements ═══ */}
      <div className={`absolute top-10 left-0 bottom-0 z-10 transition-all duration-200 ${leftPanelOpen ? 'w-56' : 'w-0'}`}>
        {leftPanelOpen && (
          <div className="h-full bg-zinc-900/95 backdrop-blur border-r border-zinc-800 overflow-y-auto p-3 space-y-4">

            {/* Projectors ON/OFF */}
            {projectors?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Power className="w-3 h-3" /> Projectors
                  </span>
                  <Button variant="ghost" size="sm" onClick={toggleAllProjectorsPower}
                    className="h-5 px-1.5 text-[10px] hover:bg-zinc-800" data-testid="viewer-toggle-all-power">
                    {projectors.every(p => p.power_on !== false) ? 'All OFF' : 'All ON'}
                  </Button>
                </div>
                <div className="space-y-1">
                  {projectors.map((proj) => (
                    <div key={proj.id} className="flex items-center justify-between py-0.5">
                      <span className="text-[10px] text-zinc-400 truncate max-w-[100px]">{proj.name || proj.label || proj.model || proj.id.slice(0,6)}</span>
                      <Switch
                        checked={proj.power_on !== false}
                        onCheckedChange={() => toggleProjectorPower(proj.id)}
                        className="scale-75"
                        data-testid={`viewer-proj-power-${proj.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Beams ON/OFF */}
            {projectors?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Lightbulb className="w-3 h-3" /> Beams
                  </span>
                  <Button variant="ghost" size="sm" onClick={toggleAllBeams}
                    className="h-5 px-1.5 text-[10px] hover:bg-zinc-800" data-testid="viewer-toggle-all-beams">
                    {projectors.every(p => p.show_beam !== false) ? 'All OFF' : 'All ON'}
                  </Button>
                </div>
                <div className="space-y-1">
                  {projectors.map((proj) => (
                    <div key={proj.id} className="flex items-center justify-between py-0.5">
                      <span className="text-[10px] text-zinc-400 truncate max-w-[100px]">{proj.name || proj.label || proj.model || proj.id.slice(0,6)}</span>
                      <Switch
                        checked={proj.show_beam !== false}
                        onCheckedChange={() => toggleProjectorBeam(proj.id)}
                        className="scale-75"
                        data-testid={`viewer-proj-beam-${proj.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Borders ON/OFF */}
            {projectors?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                    Borders
                  </span>
                </div>
                <div className="space-y-1">
                  {projectors.map((proj) => (
                    <div key={proj.id} className="flex items-center justify-between py-0.5">
                      <span className="text-[10px] text-zinc-400 truncate max-w-[100px]">{proj.name || proj.label || proj.model || proj.id.slice(0,6)}</span>
                      <Switch
                        checked={proj.show_border !== false}
                        onCheckedChange={() => toggleProjectorBorder(proj.id)}
                        className="scale-75"
                        data-testid={`viewer-proj-border-${proj.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Measurements */}
            {measurements?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                    <Ruler className="w-3 h-3" /> Measurements
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => toggleMeasurementVisibility(!showMeasurements)}
                    className="h-5 px-1.5 text-[10px] hover:bg-zinc-800" data-testid="viewer-toggle-measurements">
                    {showMeasurements ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {showMeasurements && (
                  <div className="space-y-1">
                    {measurements.map((m, i) => (
                      <div key={m.id} className="flex items-center justify-between py-0.5">
                        <span className="text-[10px] text-zinc-400 truncate max-w-[120px]">{m.label || `Measurement ${i + 1}`}</span>
                        <span className="text-[10px] font-mono text-green-400/70">
                          {m.distance != null ? m.distance.toFixed(2) + 'm' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Grid */}
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
                <Grid3X3 className="w-3 h-3" /> Grid
              </span>
              <Switch
                checked={worldSettings?.showGrid || false}
                onCheckedChange={toggleGrid}
                className="scale-75"
                data-testid="viewer-grid-toggle"
              />
            </div>
          </div>
        )}

        {/* Left panel toggle button */}
        <Button
          variant="ghost" size="sm"
          onClick={() => setLeftPanelOpen(!leftPanelOpen)}
          className="absolute top-2 -right-7 z-20 h-6 w-6 p-0 bg-zinc-900/90 hover:bg-zinc-800 border border-zinc-700 rounded"
          data-testid="viewer-left-panel-toggle"
        >
          {leftPanelOpen ? <ChevronLeft className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </Button>
      </div>

      {/* ═══ CENTER BOTTOM: Viewport Angles + Cameras ═══ */}
      {showControls && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-zinc-900/95 backdrop-blur rounded-xl border border-zinc-700 p-2.5 shadow-xl">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => handleViewChange('reset')}
                className="h-7 w-7 p-0 hover:bg-zinc-800" title="Perspective">
                <Home className="w-3.5 h-3.5" />
              </Button>
              <div className="w-px h-5 bg-zinc-700" />
              {['top','bottom','front','back','left','right'].map(v => (
                <Button key={v} variant="ghost" size="sm" onClick={() => handleViewChange(v)}
                  className="h-7 px-2 hover:bg-zinc-800 text-[11px] capitalize" title={`${v} View`}>
                  {v === 'top' && <ArrowUp className="w-3 h-3 mr-0.5" />}
                  {v === 'bottom' && <ArrowDown className="w-3 h-3 mr-0.5" />}
                  {v === 'left' && <ArrowLeft className="w-3 h-3 mr-0.5" />}
                  {v === 'right' && <ArrowRight className="w-3 h-3 mr-0.5" />}
                  {v}
                </Button>
              ))}
              {savedCameras.length > 0 && (
                <>
                  <div className="w-px h-5 bg-zinc-700" />
                  {savedCameras.map((cam, i) => (
                    <Button key={cam.id || i} variant="ghost" size="sm" onClick={() => switchToCamera(cam)}
                      className="h-7 px-2 hover:bg-zinc-800 text-[11px]" title={cam.name || `Camera ${i + 1}`}>
                      <Camera className="w-3 h-3 mr-0.5" />{cam.name || `Cam ${i + 1}`}
                    </Button>
                  ))}
                </>
              )}
            </div>
            <p className="text-[9px] text-zinc-600 text-center mt-1.5">
              Drag to orbit &bull; Scroll to zoom &bull; Right-click to pan
            </p>
          </div>
        </div>
      )}

      <Button variant="ghost" size="sm" onClick={() => setShowControls(!showControls)}
        className="absolute bottom-4 right-4 z-10 bg-zinc-900/80 hover:bg-zinc-800 text-[11px]">
        {showControls ? 'Hide Controls' : 'Show Controls'}
      </Button>

      {/* Right-side utility stack — placed BELOW the ViewCube (which sits at top-14 right-3, ~100px tall) */}
      <div className="absolute top-40 right-3 z-30 flex flex-col gap-1.5" data-testid="viewer-utility-stack">
        {/* Pan / Orbit toggle (Hand tool) — mirrors the editor's camera control mode. */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setControlMode(controlMode === 'pan' ? 'orbit' : 'pan')}
          title={controlMode === 'pan' ? 'Pan mode — click to switch to Orbit' : 'Orbit mode — click to switch to Pan'}
          data-testid="viewer-pan-toggle"
          className={`h-8 w-8 border backdrop-blur
            ${controlMode === 'pan'
              ? 'bg-fuchsia-600/80 hover:bg-fuchsia-500/80 text-white border-fuchsia-400'
              : 'bg-zinc-900/80 hover:bg-zinc-800 text-zinc-200 border-zinc-700'}`}
        >
          {controlMode === 'pan' ? <Hand className="w-4 h-4" /> : <Move3d className="w-4 h-4" />}
        </Button>

        {/* Fullscreen */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()}
          title="Toggle fullscreen"
          data-testid="viewer-fullscreen-btn"
          className="h-8 w-8 bg-zinc-900/80 hover:bg-zinc-800 text-zinc-200 border border-zinc-700 backdrop-blur"
        >
          <Maximize2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const ViewerPage = () => {
  const { shareToken } = useParams();
  return (
    <AppProvider viewerMode={true}>
      <ViewerContent shareToken={shareToken} />
    </AppProvider>
  );
};

export default ViewerPage;
